import { useRef, useState } from 'react';
import { OverlayPanel } from 'primereact/overlaypanel';
import type { MediaAssetResponse, MediaFolderResponse } from '@/core/generated/api';
import { getMediaFolderPath } from '@/entities/MediaLibrary/model/format';
import { useCanAccessPermission } from '@/shared/model/adminAccess';
import { RequestStatus } from '@/shared/model/requestStatus';
import { ActionButton } from '@/shared/ui/ActionButton/ActionButton';
import { ActionMenu } from '@/shared/ui/ActionMenu/ActionMenu';
import type { ActionMenuItem } from '@/shared/ui/ActionMenu/types';
import { InputText } from '@/shared/ui/InputText/InputText';
import { DropdownField } from '@/shared/ui/DropdownField/DropdownField';
import { DateTimeFilter } from '@/shared/ui/DateTimeFilter/DateTimeFilter';
import { Paginator } from '@/shared/ui/Paginator/Paginator';
import { RequestEmpty, RequestError, RequestLoading } from '@/shared/ui/RequestState';
import { MediaFolderTree } from './MediaFolderTree';
import { MediaFolderCards } from './MediaFolderCards';
import { MediaItems } from './MediaItems';
import { MediaEditDialog } from './MediaEditDialog';
import { MediaDeleteDialog } from './MediaDeleteDialog';
import { MediaUploadDialog } from './MediaUploadDialog';
import { MediaImportDialog } from './MediaImportDialog';
import { MediaDetailsPanel } from './MediaDetailsPanel';
import { useMediaLibrary } from './useMediaLibrary';
import classes from './MediaLibrary.module.scss';

interface MediaLibraryBrowserProps {
    onPick?: (asset: MediaAssetResponse) => void;
    onCancel?: () => void;
}

const IMAGE_TYPES = [{
    name: 'Все типы',
    id: ''
}, {
    name: 'PNG',
    id: 'image/png'
}, {
    name: 'JPEG',
    id: 'image/jpeg'
}, {
    name: 'WEBP',
    id: 'image/webp'
}, {
    name: 'GIF',
    id: 'image/gif'
}];

/** Показывает медиатеку и переиспользуется в окне выбора изображения */
export const MediaLibraryBrowser = ({ onPick, onCancel }: MediaLibraryBrowserProps) => {
    const library = useMediaLibrary();
    const [list, setList] = useState(false);
    const [filters, setFilters] = useState(false);
    const filterPanel = useRef<OverlayPanel>(null);
    const [uploading, setUploading] = useState(false);
    const [importing, setImporting] = useState(false);
    const canImport = useCanAccessPermission('admin.media-library.import');
    const selectedAssets = library.items.filter(item => library.selected.includes(item.id ?? ''));
    const childFolders = library.folders.filter(folder => (folder.parentId ?? undefined) === library.folderId);
    const currentFolder = library.folders.find(item => item.id === library.folderId);
    const busy = library.status === RequestStatus.Loading;

    /** Предлагает доступные действия над изображением */
    const actions = (asset: MediaAssetResponse): ActionMenuItem[] => [
        {
            label: 'Сведения о файле',
            icon: 'pi pi-info-circle',
            command: () => library.setDetails(asset)
        },
        {
            label: 'Переименовать',
            icon: 'pi pi-pencil',
            permission: 'admin.media-library.manage',
            command: () => library.edit('rename', [asset])
        },
        {
            label: 'Переместить',
            icon: 'pi pi-folder',
            permission: 'admin.media-library.manage',
            command: () => library.edit('move', [asset])
        },
        {
            label: 'Добавить теги',
            icon: 'pi pi-tag',
            permission: 'admin.media-library.manage',
            command: () => library.edit('tags', [asset])
        },
        {
            label: 'Скачать',
            icon: 'pi pi-download',
            permission: 'admin.media-library.download',
            disabled: library.working,
            command: () => void library.download([asset])
        },
        {
            label: 'Удалить из каталога',
            icon: 'pi pi-trash',
            permission: 'admin.media-library.delete',
            command: () => library.setDeleting([asset])
        },
    ];

    /** Предлагает действия над логической папкой */
    const folderActions = (folder: MediaFolderResponse): ActionMenuItem[] => [
        {
            label: 'Изменить папку',
            icon: 'pi pi-pencil',
            permission: 'admin.media-library.manage',
            command: () => library.edit('rename-folder', [], folder)
        },
        {
            label: 'Удалить пустую папку',
            icon: 'pi pi-trash',
            permission: 'admin.media-library.manage',
            disabled: library.working,
            command: () => void library.deleteFolder(folder)
        },
    ];

    if (!library.canView) return <RequestEmpty message="Нет доступа к медиатеке" />;

    return <section
        className={`${classes.page} ${onPick
            ? classes.picker
            : ''}`}
        aria-label="Медиатека"
    >
        <header className={classes.heading}>
            <div>
                {!onPick &&
                    <h1>Медиатека</h1>
                }
                <p>
                    {onPick
                        ? 'Выберите изображение для публикации'
                        : 'Изображения и файлы VOX'}
                </p>
            </div>
            <div className={classes.headingActions}>
                <ActionButton
                    type="button"
                    icon="pi pi-upload"
                    permission="admin.media-library.upload"
                    onClick={() => setUploading(true)}
                >
                    Загрузить файлы
                </ActionButton>
                <ActionButton
                    type="button"
                    icon="pi pi-plus-circle"
                    outlined
                    color="secondary"
                    permission="admin.media-library.manage"
                    onClick={() => library.edit('folder')}
                >
                    Новая папка
                </ActionButton>
                {canImport && !onPick &&
                    <ActionMenu
                        aria-label="Управление S3"
                        model={[{
                            label: 'Подключение к S3 и импорт',
                            icon: 'pi pi-cloud',
                            command: () => setImporting(true)
                        }]}
                    />
                }
            </div>
        </header>
        <div className={classes.catalog}>
            <div className={classes.toolbar}>
                <div className={classes.search}>
                    <i
                        aria-hidden="true"
                        className="pi pi-search"
                    />
                    <InputText
                        name="media-search"
                        value={library.search}
                        placeholder="Поиск по названию и тегам"
                        onChange={event => {
                            library.setSearch(event.target.value);
                            library.setPage(0);
                        }}
                    />
                </div>
                <ActionButton
                    type="button"
                    outlined
                    color="secondary"
                    icon="pi pi-filter"
                    aria-expanded={filters}
                    onClick={event => {
                        setFilters(value => !value);
                        filterPanel.current?.toggle(event);
                    }}
                >
                    Фильтры
                </ActionButton>
                <div className={classes.viewToggle}>
                    <ActionButton
                        type="button"
                        iconOnly
                        icon="pi pi-th-large"
                        aria-label="Сетка"
                        aria-pressed={!list}
                        color="secondary"
                        outlined={list}
                        onClick={() => setList(false)}
                    />
                    <ActionButton
                        type="button"
                        iconOnly
                        icon="pi pi-bars"
                        aria-label="Список"
                        aria-pressed={list}
                        color="secondary"
                        outlined={!list}
                        onClick={() => setList(true)}
                    />
                </div>
            </div>
            <OverlayPanel
                ref={filterPanel}
                className={classes.filterPanel}
                onShow={() => setFilters(true)}
                onHide={() => setFilters(false)}
            >
                <div className={classes.filters}>
                    <DropdownField
                        name="media-type"
                        label="Тип файла"
                        value={library.contentType}
                        options={IMAGE_TYPES}
                        onChange={event => {
                            library.setContentType(event.value as string);
                            library.setPage(0);
                        }}
                    />
                    <DateTimeFilter
                        label="Добавлен в каталог"
                        value={library.date}
                        onChange={value => {
                            library.setDate(value);
                            library.setPage(0);
                        }}
                    />
                    <label>
                        <input
                            type="checkbox"
                            checked={library.unusedOnly}
                            onChange={event => {
                                library.setUnusedOnly(event.target.checked);
                                library.setPage(0);
                            }}
                        /> Не используется в контенте</label>
                    <ActionButton
                        type="button"
                        outlined
                        color="secondary"
                        onClick={() => {
                            library.setContentType('');
                            library.setUnusedOnly(false);
                            library.setDate(null);
                            library.setPage(0);
                        }}
                    >
                        Сбросить
                    </ActionButton>
                </div>
            </OverlayPanel>
            <div className={classes.catalogBody}>
                <div>
                    {library.foldersStatus === RequestStatus.Loading &&
                        <RequestLoading message="Загрузка папок…" />
                    }
                    {library.foldersStatus === RequestStatus.Error &&
                        <RequestError
                            message={library.foldersError ?? 'Не удалось загрузить папки'}
                            onRetry={library.reloadFolders}
                        />
                    }
                    <MediaFolderTree
                        folders={library.folders}
                        selected={library.folderId}
                        onSelect={library.selectFolder}
                    />
                </div>
                <div className={classes.content}>
                    <div className={classes.breadcrumb}>
                        <span>
                            {getMediaFolderPath(library.folders, library.folderId)}
                        </span>
                        {currentFolder &&
                            <ActionMenu model={folderActions(currentFolder)} />
                        }
                        <span className={classes.count}>
                            {library.allCount} файлов</span>
                    </div>
                    {!onPick && library.selected.length > 0 &&
                        <div className={classes.bulk}>
                            <strong>Выбрано: {library.selected.length}
                            </strong>
                            <ActionButton
                                type="button"
                                text
                                color="secondary"
                                permission="admin.media-library.manage"
                                onClick={() => library.edit('move', selectedAssets)}
                            >
                                Переместить
                            </ActionButton>
                            <ActionButton
                                type="button"
                                text
                                color="secondary"
                                permission="admin.media-library.manage"
                                onClick={() => library.edit('tags', selectedAssets)}
                            >
                                Добавить теги
                            </ActionButton>
                            <ActionButton
                                type="button"
                                text
                                color="secondary"
                                permission="admin.media-library.download"
                                loading={library.working}
                                onClick={() => void library.download(selectedAssets)}
                            >
                                Скачать
                            </ActionButton>
                            <ActionButton
                                type="button"
                                text
                                color="danger"
                                permission="admin.media-library.delete"
                                onClick={() => library.setDeleting(selectedAssets)}
                            >
                                Удалить
                            </ActionButton>
                            <ActionButton
                                type="button"
                                iconOnly
                                icon="pi pi-times"
                                aria-label="Снять выбор"
                                text
                                color="secondary"
                                onClick={() => library.setSelected([])}
                            />
                        </div>
                    }
                    {!library.search && !filters && !list && childFolders.length > 0 &&
                        <MediaFolderCards
                            key={library.folderId ?? 'root'}
                            folders={childFolders}
                            onSelect={library.selectFolder}
                            actions={onPick
                                ? undefined
                                : folderActions
                            }
                        />
                    }
                    {library.actionError &&
                        <RequestError message={library.actionError} />
                    }
                    {library.status === RequestStatus.Error &&
                        <RequestError
                            message={library.error ?? 'Не удалось загрузить медиатеку'}
                            onRetry={library.reload}
                        />
                    }
                    {busy &&
                        <RequestLoading message="Загрузка медиатеки…" />
                    }
                    {!busy && library.status === RequestStatus.Success &&
                        <>
                            {library.items.length
                                ? <MediaItems
                                    items={library.items}
                                    selected={library.selected}
                                    list={list}
                                    onSelect={id => onPick
                                        ? library.setSelected(library.selected.includes(id)
                                            ? []
                                            : [id])
                                        : library.toggle(id)}
                                    onOpen={asset => onPick
                                        ? library.setSelected([asset.id ?? ''])
                                        : library.setDetails(asset)}
                                    actions={asset => onPick
                                        ? []
                                        : actions(asset)}
                                />
                                : <div className={classes.empty}>
                                    <i
                                        aria-hidden="true"
                                        className="pi pi-images"
                                    />
                                    <h3>
                                        {library.search || library.contentType || library.unusedOnly || library.date
                                            ? 'Файлы не найдены'
                                            : 'Здесь пока нет изображений'}
                                    </h3>
                                    <p>
                                        {library.search
                                            ? 'Попробуйте другое название или тег'
                                            : 'Загрузите изображения или импортируйте существующие файлы из S3'}
                                    </p>
                                </div>}
                        </>
                    }
                    <Paginator
                        first={library.page * library.pageSize}
                        rows={library.pageSize}
                        totalRecords={library.allCount}
                        rowsPerPageOptions={[12, 24, 48, 96]}
                        disabled={busy}
                        label="Страницы медиатеки"
                        onPageChange={event => {
                            library.setPage(event.page);
                            library.setPageSize(event.rows);
                        }}
                    />
                </div>
            </div>
        </div>
        {onPick &&
            <div className={classes.dialogFooter}>
                <span className={classes.note}>
                    {selectedAssets[0]?.name ?? 'Выберите изображение'}
                </span>
                {onCancel &&
                    <ActionButton
                        type="button"
                        outlined
                        color="secondary"
                        onClick={onCancel}
                    >
                        Отмена
                    </ActionButton>
                }
                <ActionButton
                    type="button"
                    disabled={selectedAssets.length !== 1 || busy}
                    onClick={() => { if (selectedAssets[0]) onPick(selectedAssets[0]); }}
                >
                    Выбрать изображение
                </ActionButton>
            </div>
        }
        {library.editing &&
            <MediaEditDialog
                {...library.editing}
                folders={library.folders}
                parentId={library.folderId}
                onClose={() => library.setEditing(undefined)}
                onSaved={library.saved}
            />
        }
        {library.deleting &&
            <MediaDeleteDialog
                assets={library.deleting}
                onClose={() => library.setDeleting(undefined)}
                onDeleted={library.saved}
            />
        }
        {uploading &&
            <MediaUploadDialog
                folderId={library.folderId}
                folderPath={getMediaFolderPath(library.folders, library.folderId)}
                onClose={() => setUploading(false)}
                onChanged={() => void library.reload()}
            />
        }
        {importing &&
            <MediaImportDialog
                folderId={library.folderId}
                onClose={() => setImporting(false)}
                onImported={() => void library.reload()}
            />
        }
        {library.details &&
            <MediaDetailsPanel
                id={library.details.id ?? ''}
                folders={library.folders}
                onClose={() => library.setDetails(undefined)}
                onMetadataUpdated={library.reload}
                onRename={asset => library.edit('rename', [asset])}
                onMove={asset => library.edit('move', [asset])}
                onDownload={asset => void library.download([asset])}
                onDelete={asset => {
                    library.setDeleting([asset]);
                    library.setDetails(undefined);
                }}
            />
        }
    </section>;
};
